import { ShieldCheck, ShieldAlert, RefreshCw } from "lucide-react";

/** Compact AutoSEO publication check report (mandatory cleanup protocol). */
export const CleanupReportPanel = ({ report, passed, onRun, busy, t }) => {
  const yes = t("admin.journal.cleanup.yes");
  const no = t("admin.journal.cleanup.no");
  const ok = t("admin.journal.cleanup.passed");
  const fail = t("admin.journal.cleanup.failed");
  const checks = report?.checks || {};
  const langs = report?.languages || {};
  const rows = [
    ["extLinks", checks.external_links_removed],
    ["refText", checks.referral_text_removed],
    ["aiRemnants", checks.ai_remnants_removed],
    ["placeholders", checks.placeholders_removed],
    ["internal", checks.internal_links_ok],
  ];
  const failures = Object.entries(langs).flatMap(([lg, s]) =>
    (s.failures || []).map((f) => ({ ...f, lang: lg })));
  return (
    <div className={`mt-8 p-4 border ${passed ? "border-emerald-400/40 bg-emerald-400/[0.04]" : "border-red-400/40 bg-red-400/[0.04]"}`} data-testid="cleanup-report-panel">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        {passed ? <ShieldCheck size={15} className="text-emerald-300" /> : <ShieldAlert size={15} className="text-red-300" />}
        <h3 className="text-[11px] tracking-[0.28em] uppercase text-bone/80">{t("admin.journal.cleanup.title")}</h3>
        <span className={`text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 border ${passed ? "border-emerald-400/50 text-emerald-300" : "border-red-400/50 text-red-300"}`} data-testid="cleanup-status-badge">
          {passed ? ok : fail}
        </span>
        <button onClick={onRun} disabled={busy} data-testid="cleanup-run-btn"
          className="ml-auto inline-flex items-center gap-2 border border-gold/50 text-gold px-4 py-2 text-[10px] tracking-[0.22em] uppercase hover:bg-gold/10 disabled:opacity-50">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> {t("admin.journal.cleanup.run")}
        </button>
      </div>
      {!report ? (
        <p className="text-[12px] text-bone/55" data-testid="cleanup-not-run">{t("admin.journal.cleanup.notRun")}</p>
      ) : (
        <>
          <ul className="text-[12px] text-bone/75 space-y-1" data-testid="cleanup-check-list">
            {rows.map(([key, c]) => (
              <li key={key} className="flex items-center gap-2">
                <span className={c?.passed ? "text-emerald-300" : "text-red-300"}>{c?.passed ? "✓" : "✗"}</span>
                {t(`admin.journal.cleanup.${key}`)}: <strong>{c?.passed ? ok : fail}</strong>
                {c?.removed > 0 && <span className="text-bone/45">({c.removed} {t("admin.journal.cleanup.removed")})</span>}
              </li>
            ))}
            {["nl", "en"].map((lg) => (
              <li key={lg} className="flex items-center gap-2" data-testid={`cleanup-lang-${lg}`}>
                <span className={langs[lg]?.clean ? "text-emerald-300" : "text-red-300"}>{langs[lg]?.clean ? "✓" : "✗"}</span>
                {t(`admin.journal.cleanup.${lg}Clean`)}: <strong>{langs[lg] ? (langs[lg].clean ? yes : no) : "—"}</strong>
              </li>
            ))}
          </ul>
          {failures.length > 0 && (
            <div className="mt-3 border-t border-white/10 pt-3" data-testid="cleanup-failures">
              <p className="text-[10px] tracking-[0.24em] uppercase text-red-300 mb-2">{t("admin.journal.cleanup.failures")}</p>
              <ul className="text-[11px] text-bone/60 space-y-1 font-mono">
                {failures.slice(0, 10).map((f, i) => (
                  <li key={i}>[{f.lang}/{f.field} · {f.check}] “{f.passage}”</li>
                ))}
              </ul>
            </div>
          )}
          {(report.review_flags || []).length > 0 && (
            <div className="mt-3 border-t border-white/10 pt-3" data-testid="cleanup-review-flags">
              <p className="text-[10px] tracking-[0.24em] uppercase text-amber-300 mb-2">{t("admin.journal.cleanup.reviewFlags")}</p>
              <ul className="text-[11px] text-bone/60 space-y-1">
                {report.review_flags.slice(0, 6).map((f, i) => <li key={i}>“{f.passage}”</li>)}
              </ul>
            </div>
          )}
        </>
      )}
      {!passed && <p className="mt-3 text-[11px] text-red-300/90" data-testid="cleanup-blocked-note">{t("admin.journal.cleanup.blocked")}</p>}
    </div>
  );
};

export default CleanupReportPanel;
